// HB-1946: intentional 4xx validation responses from POST /api/cart/dtf_add.
// Body must parse as a JSON object with `success === false` and EVERY non-
// `success` entry must be allowlisted. These are deliberate server-side guard
// decisions (per-artwork dimension validation, all-attachments-missing
// rejection) — surfaced to the user in the UI, so Honeybadger noise adds
// nothing actionable.
//
// EVERY-entry rule: `Dtf::AddToCart#errors_formatted` aggregates errors
// across all artworks into one body, e.g.
// `{success:false, "42048221":"Artwork is smaller than 1 x 1",
//                   "42048441":"Invalid variant ID: foo"}`.
// A "ANY entry matches" predicate would suppress that mixed body and hide the
// `Invalid variant ID` bug. Requiring EVERY entry to be allowlisted prevents
// false suppression while still dropping the homogeneous-benign bodies that
// fault 130662261 is built from.
//
// Producer-side observability:
//  - The dimension validation produces `errors[id] = "Artwork is smaller|
//    larger than W x H"` per artwork. Per-request audit is already captured
//    by `Ledger.log(current_order, 'request_info_on_cart_add', ...)` at
//    `packages/core/app/controllers/spree/api/orders_controller.rb:1774`.
//  - The all-attachments-missing path additionally calls
//    `AppMonitoring.notify('dtf_add_to_cart_all_attachments_missing', ...)`
//    at `packages/dtf/app/services/dtf/add_to_cart.rb:681` (HB-1946 backend
//    change). Required precondition for the client-side
//    `artwork_attachments` suppression so an upload pipeline regression is
//    not silently dropped.
//
// Extend the allowlist with team review when a new benign body shape appears.

// Anchored regex against the integer-constant format produced by
// `Dtf::DimensionValidator#min_error_message` and `#max_error_message`. The
// constants `DTF_MIN_WIDTH`, `DTF_MIN_HEIGHT`, `DTF_MAX_WIDTH`,
// `DTF_GANG_MAX_HEIGHT`, and `DTF_MAX_HEIGHT` are integers, so the regex is
// anchored to `\d+` (no decimal point). If those constants ever become floats
// the regex must be widened.
export const DTF_ADD_VALIDATION_BENIGN_ARTWORK_MESSAGE =
  /^Artwork is (smaller|larger) than \d+ x \d+$/;
export const DTF_ADD_VALIDATION_BENIGN_ARTWORK_ATTACHMENTS_VALUE = "Upload failed. Please retry";

// DtfArtwork primary keys start at 1; the leading-zero rule rejects "0" and
// "012345" alike. Pure integer-string match: at least one digit, never starting
// with 0. Numeric keys with non-digit characters (e.g. "0123456789abcdef") are
// rejected.
const ARTWORK_ID_KEY_PATTERN = /^[1-9]\d*$/;

const isAllowlistedEntry = (key, value) => {
  if (typeof value !== "string") return false;
  if (ARTWORK_ID_KEY_PATTERN.test(key)) {
    return DTF_ADD_VALIDATION_BENIGN_ARTWORK_MESSAGE.test(value);
  }
  if (key === "artwork_attachments") {
    return value === DTF_ADD_VALIDATION_BENIGN_ARTWORK_ATTACHMENTS_VALUE;
  }
  return false;
};

export const hasDtfAddValidationErrorBody = body => {
  if (typeof body !== "string" || body.length === 0) return false;
  const trimmed = body.trim();
  if (!trimmed.startsWith("{")) return false;
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return false;
  if (parsed.success !== false) return false;

  const entries = Object.entries(parsed).filter(([key]) => key !== "success");
  // Bare `{success:false}` is too sparse to count as a deliberate benign
  // response — keep reporting. Mirrors the `hasApplication404FailureBody`
  // posture in is-relevant-browser-error.js.
  if (entries.length === 0) return false;

  return entries.every(([key, value]) => isAllowlistedEntry(key, value));
};
